/**
 * One index over every entity in the current snapshot, for the command palette.
 *
 * Matching happens against the snapshot already in the client, so it costs no
 * authority round trip and cannot show anything the authority did not send.
 */

import { projected } from './product.js';

import type { SurfaceSnapshot } from '../generated/surface.js';
import { changeDetailHref, codeHistoryHref, projectHref } from './nav.js';

export type SearchHit = {
  kind: string;
  id: string;
  label: string;
  href: string;
};

export function indexSnapshot(snapshot: SurfaceSnapshot): SearchHit[] {
  const hits: SearchHit[] = [];
  for (const change of snapshot.recent_changes) {
    hits.push({
      kind: 'change',
      id: change.id,
      label: `${change.producer} — ${change.intent}`,
      href: changeDetailHref(change.id), // shared inspector address, not an inlined query string
    });
  }
  for (const evidence of snapshot.recent_evidence) {
    hits.push({
      kind: 'evidence',
      id: evidence.digest,
      label: `${evidence.claim} — ${evidence.verdict} via ${evidence.oracle}`,
      href: projectHref('checks'),
    });
  }
  for (const certificate of snapshot.certificates) {
    hits.push({
      kind: 'certificate',
      id: certificate.digest,
      label: `epoch ${String(certificate.epoch)} accepted ${String(certificate.accepted_count)} change(s)`,
      href: codeHistoryHref(),
    });
  }
  for (const state of snapshot.states) {
    hits.push({
      kind: 'state',
      id: state.root,
      label: `${String(state.file_count)} file(s)`,
      href: codeHistoryHref(),
    });
  }
  for (const run of projected(snapshot, 'fabric_runs') ?? []) {
    hits.push({
      kind: 'fabric run',
      id: run.digest,
      label: `${run.decision} — ${String(run.reused)} reused, ${String(run.executed)} executed`,
      href: projectHref('checks', { view: 'oracles' }),
    });
  }
  return hits;
}
